/**
 * Formats the payload to be sent to the backend when saving the wave import's filters configuration.
 * The payload is structured in a way that each changed section contains its filters and parameters, along with the branchId for context.
 * If no form has changed, it returns undefined to avoid unnecessary API calls.
 *
 * @param {Object} payload - The raw payload from the generic form atom, containing all sections and filters with their parameters and change status.
 * @param {number} branchId - The ID of the branch for which the configuration is being saved, used for context in the backend.
 * @returns {Array|undefined}
 */

const cleanPayload = (obj) => {
  if (Array.isArray(obj)) {
    return obj.map(cleanPayload);
  } else if (obj !== null && typeof obj === 'object') {
    const cleanObj = {};
    const keys = Object.keys(obj);

    for (const key of keys) {
      if (key.toLowerCase() === 'formchanged') continue;

      const camelKey = key.charAt(0).toLowerCase() + key.slice(1);
      if (key !== camelKey && keys.includes(camelKey)) continue;

      cleanObj[key] = cleanPayload(obj[key]);
    }

    return cleanObj;
  }
  return obj;
};

export default (payload, branchId) => {
  if (!payload) return;

  const results = Object.keys(payload)
    ?.filter((sectionId) => sectionId.toLowerCase() !== 'formchanged')
    ?.map((sectionId) =>
      Object.keys(payload[sectionId])
        ?.filter((filterId) => filterId.toLowerCase() !== 'formchanged')
        ?.map((filterId) => {
          const filterData = payload[sectionId][filterId];

          // Check if data changed
          const isChanged = filterData?.formChanged || filterData?.FormChanged;

          // Clean the data by removing formChanged and duplicates
          const cleanedData = cleanPayload(filterData);

          // Check if there are default values
          const hasData = Object.keys(cleanedData ?? {}).length > 0;

          if (isChanged || hasData) {
            return {
              sectionId,
              filterId,
              branchId,
              storedProcedureParameters: JSON.stringify(cleanedData),
            };
          }

          return null;
        })
    )
    ?.flat()
    ?.filter(Boolean);

  return results?.length > 0 ? results : undefined;
};
